// Shared "Forgot password?" flow for both login pages (Login + StudioLogin). Two steps against whichever pool
// the parent signs into: request a code (Cognito emails it), then confirm code + new password. Parents open it
// straight on the confirm step when sign-in fails with PasswordResetRequiredException — that's the state an
// admin's "reset password" button leaves an account in, and the emailed code is useless without this screen.
import React, { useState } from 'react';
import { Button, TextInput, Field } from '../ui/primitives';

// Cognito error codes → copy a user can act on. Anything unmapped falls back to Cognito's own message.
function resetErrorMessage(err, fallback) {
  switch (err?.code) {
    case 'CodeMismatchException':
      return 'That code is incorrect. Check the email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Send a new one below.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'NotAuthorizedException':
      // Cognito's "User password cannot be reset in the current state" — invited users who never signed in
      // (FORCE_CHANGE_PASSWORD) or disabled accounts.
      return "This account can't reset its password yet. If you were just invited, sign in with the temporary " +
        'password from your invite email, or ask an admin to resend the invite.';
    case 'InvalidParameterException':
      return err.message?.includes('verified')
        ? 'This account has no verified email to send a code to. Ask an admin to reset your password.'
        : err.message || fallback;
    default:
      return err?.message || fallback;
  }
}

function PasswordResetForm({
  initialUsername = '',
  initialStep = 'request',
  notice = '',
  requestReset,
  confirmReset,
  onDone,
  onCancel,
}) {
  const [step, setStep] = useState(initialStep);
  const [username, setUsername] = useState(initialUsername);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [info, setInfo] = useState(notice);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const sendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const destination = await requestReset(username.trim());
      setInfo(`We emailed a verification code to ${destination || 'your email'}.`);
      setStep('confirm');
    } catch (err) {
      if (err?.code === 'UserNotFoundException') {
        // Don't reveal which emails have accounts — behave as if a code was sent.
        setInfo('If an account exists for that email, a verification code is on its way.');
        setStep('confirm');
      } else {
        setError(resetErrorMessage(err, 'Could not send a code'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = (e) => {
    e.preventDefault();
    sendCode();
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmReset(username.trim(), code.trim(), newPassword);
      await onDone(username.trim(), newPassword);
    } catch (err) {
      setError(resetErrorMessage(err, 'Could not reset password'));
      setLoading(false);
    }
  };

  const infoBlock = info ? (
    <div className="mb-4 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-800 text-sm px-3 py-2">
      {info}
    </div>
  ) : null;

  const errorBlock = error ? (
    <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">
      {error}
    </div>
  ) : null;

  const linkClass = 'text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50';

  if (step === 'request') {
    return (
      <>
        <p className="text-sm text-slate-600 mb-4 text-center">
          Enter your email and we'll send you a code to reset your password.
        </p>
        {infoBlock}
        {errorBlock}
        <form onSubmit={handleRequest} className="space-y-3">
          <Field label="Email">
            <TextInput
              type="text"
              placeholder="you@company.com"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              autoFocus
            />
          </Field>
          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            {loading ? 'Sending…' : 'Send code'}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <button type="button" className={linkClass} onClick={onCancel}>Back to sign in</button>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="text-sm text-slate-600 mb-4 text-center">
        Reset the password for <span className="font-medium text-slate-900 break-all">{username}</span>
      </p>
      {infoBlock}
      {errorBlock}
      <form onSubmit={handleConfirm} className="space-y-3">
        <Field label="Verification code">
          <TextInput
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
          />
        </Field>
        <Field label="New password">
          <TextInput
            type="password"
            autoComplete="new-password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </Field>
        <Button type="submit" size="lg" className="w-full" disabled={loading}>
          {loading ? 'Resetting…' : 'Reset password & sign in'}
        </Button>
      </form>
      <div className="mt-4 flex items-center justify-between gap-3">
        <button type="button" className={linkClass} onClick={onCancel}>Back to sign in</button>
        <button type="button" className={linkClass} onClick={sendCode} disabled={loading}>Send a new code</button>
      </div>
    </>
  );
}

export default PasswordResetForm;
